import dayjs from 'dayjs'
import * as React from 'react'

import TripService from '../services/TripService'

const TripContext = React.createContext()

/**
 * Provider component for trip management
 */
export function TripProvider({ children }) {
  const [upcomingTrips, setUpcomingTrips] = React.useState([])
  const [pastTrips, setPastTrips] = React.useState([])
  const [upcomingTripsLoaded, setUpcomingTripsLoaded] = React.useState(false)
  const [pastTripsLoaded, setPastTripsLoaded] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  // 获取即将到来的旅行
  const fetchUpcomingTrips = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // 通过 TripService 获取今天及以后的旅行
      const response = await TripService.getTrips({
        // 假设 TripService.getTrips 接受日期筛选参数
        // 根据实际 API 调整参数
        dateAfter: dayjs().startOf('day').valueOf(),
      })

      setUpcomingTrips(response)
      setUpcomingTripsLoaded(true)
    } catch (err) {
      console.error('Error fetching upcoming trips:', err)
      setError(err.message || 'Failed to fetch upcoming trips')
    } finally {
      setLoading(false)
    }
  }, [])

  // 获取过去的旅行
  const fetchPastTrips = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // 通过 TripService 获取过去的旅行
      const response = await TripService.getTrips({
        // 假设 TripService.getTrips 接受日期筛选参数
        // 根据实际 API 调整参数
        dateBefore: dayjs().startOf('day').valueOf(),
      })

      setPastTrips(response)
      setPastTripsLoaded(true)
    } catch (err) {
      console.error('Error fetching past trips:', err)
      setError(err.message || 'Failed to fetch past trips')
    } finally {
      setLoading(false)
    }
  }, [])

  // 注册用户参加旅行
  const registerForTrip = React.useCallback(
    async (tripId, registrationData) => {
      try {
        setLoading(true)
        setError(null)
        const response = await TripService.registerTrip(tripId, registrationData)

        // 注册成功后刷新即将到来的旅行列表
        await fetchUpcomingTrips()
        return response
      } catch (err) {
        console.error('Error registering for trip:', err)
        setError(err.message || 'Failed to register for trip')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchUpcomingTrips],
  )

  // 取消旅行注册
  const cancelTripRegistration = React.useCallback(
    async (tripId, registrationId) => {
      try {
        setLoading(true)
        setError(null)
        const response = await TripService.cancelRegistration(tripId, registrationId)

        // 取消成功后刷新旅行列表
        await fetchUpcomingTrips()
        return response
      } catch (err) {
        console.error('Error cancelling trip registration:', err)
        setError(err.message || 'Failed to cancel trip registration')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchUpcomingTrips],
  )

  const value = React.useMemo(
    () => ({
      upcomingTrips,
      pastTrips,
      upcomingTripsLoaded,
      pastTripsLoaded,
      loading,
      error,
      fetchUpcomingTrips,
      fetchPastTrips,
      registerForTrip,
      cancelTripRegistration,
    }),
    [upcomingTrips, pastTrips, upcomingTripsLoaded, pastTripsLoaded, loading, error, fetchUpcomingTrips, fetchPastTrips, registerForTrip, cancelTripRegistration],
  )

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>
}

/**
 * Hook for using trip context
 */
export function useTrip() {
  const context = React.useContext(TripContext)
  if (!context) {
    throw new Error('useTrip must be used within a TripProvider')
  }
  return context
}
